import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { signaturesAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeHtml } from '../../lib/sanitize';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'align', 'list', 'bullet',
  'link', 'image'
];

export default function SignatureManager() {
  const [signatures, setSignatures] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', content_html: '', is_default: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadSignatures(); }, []);

  const loadSignatures = async () => {
    try {
      const res = await signaturesAPI.getSignatures();
      setSignatures(res.data.signatures);
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setLoading(true);
    try {
      const data = {
        name: form.name,
        content_html: form.content_html,
        content_text: form.content_html.replace(/<[^>]+>/g, ''),
        is_default: form.is_default,
      };
      if (editing) {
        await signaturesAPI.updateSignature(editing.id, data);
        toast.success('Signature mise à jour');
      } else {
        await signaturesAPI.createSignature(data);
        toast.success('Signature créée');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', content_html: '', is_default: false });
      loadSignatures();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setLoading(false); }
  };

  const handleEdit = (sig) => {
    setEditing(sig);
    setForm({ name: sig.name, content_html: sig.content_html, is_default: sig.is_default });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await signaturesAPI.deleteSignature(id);
      toast.success('Signature supprimée');
      loadSignatures();
    } catch { toast.error('Erreur de suppression'); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', content_html: '', is_default: false });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="signature-manager">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Signatures email</h3>
        <Button onClick={openNew} size="sm" data-testid="new-signature-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle signature
        </Button>
      </div>

      {signatures.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucune signature. Créez-en une pour l'ajouter à vos emails.</p>
      ) : (
        <div className="grid gap-3">
          {signatures.map((sig) => (
            <Card key={sig.id} data-testid={`signature-card-${sig.id}`}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {sig.is_default && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                  {sig.name}
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(sig)} data-testid={`edit-sig-${sig.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(sig.id)} data-testid={`delete-sig-${sig.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-2 px-4">
                {/* Bug fix: sanitize signature HTML to prevent XSS */}
                <div className="text-xs text-muted-foreground border rounded p-2 max-h-20 overflow-hidden" dangerouslySetInnerHTML={{ __html: sanitizeHtml(sig.content_html) }} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la signature' : 'Nouvelle signature'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Signature professionnelle"
                data-testid="sig-name-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Contenu</Label>
              <div className="border rounded-md overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={form.content_html}
                  onChange={(val) => setForm(prev => ({ ...prev, content_html: val }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Composez votre signature..."
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_default}
                onCheckedChange={(val) => setForm(prev => ({ ...prev, is_default: val }))}
                data-testid="sig-default-switch"
              />
              <Label>Signature par défaut</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={loading} data-testid="sig-save-btn">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
